import { useState } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError, errMsg } from '../lib/api';
import { useDocumentTitle, useTenant } from '../lib/hooks';
import { Button, ErrorState, Loading, useConfirm } from '../components/ui';
import { useToast } from '../components/feedback';
import { Comments, ReportHtml, useReportCss } from '../components/ReportBody';
import { PdfButton } from '../components/PdfButton';
import { ShareSheet } from '../components/ShareSheet';

interface ReportData {
  html: string;
  visit: { id: string; numero: number; restaurantId: string; restaurantNome: string; finishedAt: number };
  canShare: boolean;
  canComment: boolean;
  canWrite: boolean;
  nota: { final: number | null; mediaRestaurante: number | null; setores: Array<{ setor: string; nota: number | null }> };
}

export function ReportPage() {
  const { tid, vid } = useParams() as { tid: string; vid: string };
  const nav = useNavigate();
  const [sharing, setSharing] = useState(false);
  const css = useReportCss();
  const tenant = useTenant(tid);
  const qc = useQueryClient();
  const toast = useToast();
  const confirm = useConfirm();
  const q = useQuery({ queryKey: ['report', tid, vid], queryFn: () => api.get<ReportData>(`/api/t/${tid}/visits/${vid}/report`) });
  useDocumentTitle(q.data ? `Relatório nº ${q.data.visit.numero} · ${q.data.visit.restaurantNome}` : 'Relatório');

  if (q.error instanceof ApiError && q.error.code === 'visit_in_progress') return <Navigate to={`/t/${tid}/visita/${vid}`} replace />;
  const back = () => (window.history.length > 1 ? nav(-1) : nav(q.data ? `/t/${tid}/restaurantes/${q.data.visit.restaurantId}` : `/t/${tid}`));

  return (
    <div style={{ minHeight: '100vh' }}>
      <header className="topbar">
        <div className="topbar-in narrow">
          <Button size="xs" onClick={back} style={{ height: 36 }}>← Voltar</Button>
          <div className="flex1" />
          {q.data?.canShare && <Button size="xs" variant="accent" style={{ height: 36 }} onClick={() => setSharing(true)}>Enviar</Button>}
          {q.data && <a className="btn xs" style={{ height: 36 }} href={`/api/t/${tid}/visits/${vid}/planilha`} download>Planilha</a>}
          {q.data && <PdfButton endpoint={`/api/t/${tid}/visits/${vid}/pdf`} printUrl={`/r/visit/${vid}?print=1`} />}
        </div>
      </header>
      <main className="page narrow anim-fade">
        {css.data && <style>{css.data}</style>}
        {q.error && <ErrorState message={errMsg(q.error)} onRetry={() => q.refetch()} />}
        {!q.data && !q.error && <Loading rows={4} height={140} />}
        {q.data && (
          <div className="report-wrap">
            {q.data.canWrite && (
              <div className="row wrap gap-8 card" style={{ padding: '10px 12px', alignItems: 'center' }}>
                <span className="small flex1 wrapline">
                  {q.data.nota.final !== null ? <>Nota da visita <strong style={{ color: 'var(--text)' }}>{q.data.nota.final}</strong></> : 'Sem nota'}
                  {q.data.nota.setores.some((s) => s.nota !== null) && ` (média de ${q.data.nota.setores.filter((s) => s.nota !== null).length} setores)`}
                  {q.data.nota.mediaRestaurante !== null && <> · média da unidade <strong style={{ color: 'var(--text)' }}>{q.data.nota.mediaRestaurante}</strong></>}
                </span>
                <Button size="sm" variant="dark" onClick={() => nav(`/t/${tid}/visita/${vid}`)}>Editar relatório</Button>
              </div>
            )}
            <ReportHtml
              html={q.data.html}
              onDeletePhoto={tenant.data?.can.writeVisits ? async (pid) => {
                await api.del(`/api/t/${tid}/visits/${vid}/photos/${pid}`);
                await q.refetch();
                qc.invalidateQueries({ queryKey: ['photos', tid] });
                toast('Foto excluída. O PDF será refeito.');
              } : undefined}
            />
            <Comments base={`/api/t/${tid}/visits/${vid}`} canComment={q.data.canComment} />
            <div className="row between wrap gap-8" style={{ padding: '4px 2px' }}>
              <Link to={`/t/${tid}/restaurantes/${q.data.visit.restaurantId}/acompanhamento`} className="link-btn">Ver acompanhamento da unidade →</Link>
              <a href={`/r/visit/${vid}`} target="_blank" rel="noreferrer" className="link-btn muted">Abrir versão para impressão</a>
            </div>
            {tenant.data?.can.writeVisits && (
              <div style={{ textAlign: 'center', marginTop: 12 }}>
                <button
                  className="link-btn muted u"
                  onClick={async () => {
                    const ok = await confirm.ask({
                      title: `Excluir o relatório nº ${q.data!.visit.numero}?`,
                      message: 'Apaga a visita, as fotos e o PDF de forma definitiva. Pendências abertas nesta visita somem; as que ela resolveu voltam a ficar abertas.',
                      cta: 'Excluir relatório',
                      danger: true,
                    });
                    if (!ok) return;
                    try {
                      await api.del(`/api/t/${tid}/visits/${vid}`);
                      for (const k of ['home', 'restaurant', 'restaurants', 'tracking', 'photos', 'visits', 'period']) qc.invalidateQueries({ queryKey: [k, tid] });
                      toast('Relatório excluído');
                      nav(`/t/${tid}/restaurantes/${q.data!.visit.restaurantId}`, { replace: true });
                    } catch (e) {
                      toast(errMsg(e), 'err');
                    }
                  }}
                >
                  Excluir relatório
                </button>
              </div>
            )}
          </div>
        )}
      </main>
      <ShareSheet tid={tid} target={sharing ? { type: 'visit_report', id: vid } : null} onClose={() => setSharing(false)} />
      {confirm.node}
    </div>
  );
}
